import type { Locale } from '@assist/i18n';

/**
 * Format tanggal, waktu, dan angka per lokal.
 *
 * Token `locale.format` menetapkan bentuknya: `09:47` (en) vs `09.47 WIB` (id),
 * `1,842` vs `1.842`, `99.94%` vs `99,94%`. Ini bukan detail kosmetik — angka
 * desimal yang salah baca di laporan dampak adalah angka yang salah.
 */
const ZONE = 'Asia/Jakarta';

export function formatDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: ZONE,
  }).format(d);
}

/**
 * BULAN, bukan hari — untuk periode pemakaian (`UsageReport.period_month`).
 *
 * Kontraknya mengirim tanggal pertama bulan itu (`2026-09-01`) karena ia bertipe `date`,
 * dan `formatDate()` merendernya sebagai "01 Sep 2026". Kalimat "Dihitung per meter, untuk
 * 01 Sep 2026" membaca seperti pemakaian SATU HARI, yang bukan yang diukurnya. Ia fungsi
 * tersendiri dan bukan parameter pada `formatDate()`, supaya pemanggil yang memang ingin
 * tanggal tidak dapat memperolehnya secara tidak sengaja.
 */
export function formatMonth(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: ZONE,
  }).format(d);
}

export function formatDateTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = formatDate(iso, locale);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ZONE,
  }).format(d);
  // Bahasa Indonesia memakai titik sebagai pemisah jam dan menyebut zona waktu.
  return locale === 'id-ID' ? `${date}, ${time.replace(':', '.')} WIB` : `${date}, ${time}`;
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** Jarak waktu relatif, untuk jejak "terakhir diperbarui". */
export function relativeTime(iso: string, locale: Locale, t: (k: string, p?: never) => string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return t('time.relative.justNow');
  if (minutes < 60) return t('time.relative.minutes', { n: minutes } as never);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.relative.hours', { n: hours } as never);
  return t('time.relative.days', { n: Math.floor(hours / 24) } as never);
}

/** Tenggat yang sudah lewat — faset OVERDUE, bukan state. */
export function isOverdue(dueAt: string | null, closedAt: string | null): boolean {
  if (!dueAt || closedAt) return false;
  return new Date(dueAt).getTime() < Date.now();
}

/**
 * Nilai uang, dengan mata uangnya.
 *
 * Dipakai kartu *Verified impact* pada Dasbor (audit L-02 / D-08): satu-satunya angka
 * di layar itu yang menjawab "apa untungnya" kepada pimpinan pabrik. Ia TIDAK memakai
 * desimal — rupiah dalam jutaan dengan dua angka di belakang koma menambah dua karakter
 * yang tidak mengubah satu keputusan pun, dan kartu statistik hanya punya satu baris.
 *
 * `amount` datang dari server sebagai angka; `v_verified_benefit` sudah menyaring klaim
 * yang belum disahkan keuangan, jadi tidak ada penyaringan kedua di sini.
 */
export function formatMoney(amount: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Tanggal RINGKAS `20 Sep` — kolom `Due` daftar temuan (audit L-03).
 *
 * `formatDate()` menghasilkan `Sep 30, 2026`, dan di kolom selebar ± 90 px itu
 * membungkus menjadi tiga baris — diukur pada `03-findings-list-en.png`. Figma `8:2`
 * menulis `20 Sep` dalam satu baris, dan tahun hanya muncul bila BUKAN tahun berjalan,
 * karena tahun yang selalu sama adalah lima karakter yang tidak pernah membedakan apa pun.
 */
export function formatDateShort(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const tahunIni = d.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(tahunIni ? {} : { year: 'numeric' }),
    timeZone: ZONE,
  }).format(d);
}
